import i18n from '../i18n/i18n.util';

export interface CustomErrorInitOptions {
  /** The error message */
  message: string;

  /** The internal error code */
  code: string;

  /** The http status code */
  statusCode?: number;

  /** Expose the error to the user? */
  expose?: boolean;

  /** The original error */
  originalError?: Error;
}

export class CustomError extends Error {
  /**
   * List of all known errors
   *
   * WARNING: in order to ensure support for earlier versions that are still in
   * use and to keep the history intact, please do not remove any existing error
   * or reuse an error code for a different use case.
   */
  static readonly errors = {
    /** Application errors */
    app: {
      General: {
        message: 'General Application Error',
        code: 'A100',
      },
    },

    /** Backend errors */
    backend: {
      General: {
        message: 'General Backend Error',
        code: 'B100',
      },
      // Auth Errors
      Auth: {
        message: 'Backend Auth Error',
        code: 'B200',
      },
      AuthSignIn: {
        message: 'Sign In Error',
        code: 'B201',
      },
      AuthSignOut: {
        message: 'Sign Out Error',
        code: 'B202',
      },
      AuthSignUp: {
        message: 'Sign Up Error',
        code: 'B203',
      },
      AuthVerification: {
        message: 'Verification Error',
        code: 'B204',
      },
      AuthPasswordUpdate: {
        message: 'Password Update Error',
        code: 'B205',
      },
      // Data Errors
      Data: {
        message: 'Backend Data Error',
        code: 'B300',
      },
      DataInvalidUID: {
        message: 'Invalid UID',
        code: 'B301',
      },
      DataNoDB: {
        message: 'No Database',
        code: 'B302',
      },
      DataReadError: {
        message: 'Data Read Error',
        code: 'B303',
      },
      DataDocumentReadError: {
        message: 'Data Document Read Error',
        code: 'B304',
      },
      DataCollectionReadError: {
        message: 'Data Collection Read Error',
        code: 'B305',
      },
      DataNotFound: {
        message: 'Data Not Found',
        code: 'B306',
      },
      DataWriteError: {
        message: 'Data Write Error',
        code: 'B307',
      },
    },

    /** Neural network errors */
    network: {
      General: {
        message: 'General Neural Network Error',
        code: 'N100',
      },
      // Auth Errors
      Auth: {
        message: 'Neural Network Auth Error',
        code: 'N200',
      },
      // Data Errors
      Data: {
        message: 'Neural Network Data Error',
        code: 'N300',
      },
      DataInvalidError: {
        message: 'Neural Network Invalid Data Error',
        code: 'N301',
      },
    },

    /** User/input errors */
    user: {
      General: {
        message: 'General User Error',
        code: 'U100',
      },
      // Auth Errors
      Auth: {
        message: 'Auth Error',
        code: 'U200',
      },
      AuthInvalidLogin: {
        message: 'Invalid Login Data',
        code: 'U201',
      },
      AuthWeakPassword: {
        message: 'Weak Password',
        code: 'U202',
      },
      AuthUserAlreadyExists: {
        message: 'User Already Exists',
        code: 'U203',
      },
      AuthAccountDisabled: {
        message: 'User Is Disabled',
        code: 'U204',
      },
      AuthUnauthorized: {
        message: 'Unauthorized',
        code: 'U205',
      },
      AuthForbidden: {
        message: 'Forbidden',
        code: 'U206',
      },
      // Data Errors
      Data: {
        message: 'Data Error',
        code: 'U300',
      },
      DataInvalidSignUpData: {
        message: 'Invalid Sign-Up Data',
        code: 'U301',
      },
      DataInvalidAccountData: {
        message: 'Invalid Account Data',
        code: 'U302',
      },
      DataInvalidPassword: {
        message: 'Invalid Password',
        code: 'U303',
      },
      DataInvalidAnalysisData: {
        message: 'Invalid Analysis Data',
        code: 'U304',
      },
    },
  };

  /** The error's name (generally the error code) */
  public name: string;

  /** The error message */
  public message: string;

  /**
   * The internal (4 digit) error code using the following schema:
   *        {1x char}{3x digit} => A100
   *
   * The character is used to determine the error category as follows:
   *  - `A`: The error occurred within the App (UI)
   *  - `B`: The error occurred within the Backend/API
   *  - `N`: The error occurred within the Neural Network Backend/API
   *  - `U`: The error occurred based on the User's input
   *
   *
   * The 3 digit error code is meant to be incremented for each unique error
   * (defined in `CustomError.errors`). Generally `100` is reserved for a
   * "general error" in each category. Other than that just use the next
   * increment of the latest error's code for any new error.
   */
  public code: string;

  /** The (optional) http status code */
  public statusCode?: number;

  /** Expose the error to the user? */
  public expose = true;

  /** The original error */
  public originalError?: Error;

  /**
   * Create a new custom error
   *
   * @example
   *  throw new CustomError({ ...CustomError.errors.app.General, originalError: error });
   */
  public constructor({
    code,
    message,
    expose = true,
    statusCode,
    originalError,
  }: CustomErrorInitOptions) {
    super(message);
    this.name = 'CustomError';

    this.code = code;
    this.message = message;
    this.expose = expose;
    this.statusCode = statusCode;
    this.originalError = originalError;

    if (originalError && originalError.stack) {
      this.stack = originalError.stack;
    }

    /**
     * Set the prototype explicitly
     * @see https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
     */
    Object.setPrototypeOf(this, CustomError.prototype);

    /** @todo consider reporting all errors to an api */
    // console.error(this.toString(), originalError);
  }

  /**
   * Generates a string representing the error
   *
   * @example
   *  new CustomError().toString();
   */
  public toString(): string {
    const { code, statusCode, message, originalError } = this;
    const errorCode = statusCode ? `${code}-${statusCode}` : code;
    const original = originalError ? ` (${originalError.message})` : '';
    const translationKey = `errors.${code}`;

    return i18n.exists(translationKey)
      ? i18n.t(translationKey)
      : `${errorCode}: ${message}${original}`;
  }
}
